import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { UserCheck, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { usePendingUsers } from '@/features/users/hooks';
import type { PendingUser } from '@/features/users/types';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  triggerHapticFeedback,
} from '../utils/adminAnimations';
import { PendingUserApprovalModal } from './PendingUserApprovalModal';
import {
  PageHeader,
  StatCard,
  CardSkeleton,
  EmptyState,
  ErrorState,
} from '@/components/ui';

export function UserApprovalsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: pending, isLoading, isError } = usePendingUsers();
  const [selectedUser, setSelectedUser] = useState<PendingUser | null>(null);

  // Auto-open approval modal when navigated with ?userId= (e.g. from notification)
  useEffect(() => {
    const userId = searchParams.get('userId');
    if (!userId || !pending) return;
    const match = pending.find((u) => u.id === userId);
    if (match) {
      setSelectedUser(match);
      // Clear the query param so refreshing doesn't re-open
      setSearchParams({}, { replace: true });
    }
  }, [pending, searchParams, setSearchParams]);

  const count = pending?.length ?? 0;

  const handleCardClick = (user: PendingUser) => {
    triggerHapticFeedback();
    setSelectedUser(user);
  };

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-4 md:space-y-6 pb-24"
    >
      {/* Header */}
      <PageHeader
        icon={<UserCheck className="w-7 h-7" />}
        title={t('userApprovals.title')}
      />

      {/* Stats */}
      <motion.div variants={staggerItem} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <StatCard
          icon={<Clock className="w-full h-full" />}
          label={t('userApprovals.pendingCount')}
          value={count}
        />
      </motion.div>

      {/* List */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </motion.div>
        ) : isError ? (
          <ErrorState message={t('common.error')} />
        ) : count === 0 ? (
          <EmptyState
            icon={<UserCheck className="w-full h-full" strokeWidth={1} />}
            message={t('userApprovals.empty')}
          />
        ) : (
          <motion.div
            key="content"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {pending!.map((user) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                onClick={() => handleCardClick(user)}
                className="flex items-center w-full py-3 ps-5 pe-4 rounded-xl border border-white/30 glass-surface cursor-pointer transition-transform active:scale-[0.98]"
                style={{ boxShadow: ADMIN_COLORS.shadow }}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Avatar placeholder */}
                  <div
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-sm"
                    style={{
                      backgroundColor: ADMIN_COLORS.warningBg,
                      color: ADMIN_COLORS.warningText,
                      border: `1px solid ${ADMIN_COLORS.border}`,
                    }}
                  >
                    {(user.firstName?.[0] ?? user.email[0]).toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm md:text-base text-neutral-800 truncate">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Approval modal */}
      {selectedUser && (
        <PendingUserApprovalModal
          isOpen={!!selectedUser}
          onClose={() => setSelectedUser(null)}
          user={selectedUser}
        />
      )}
    </motion.div>
  );
}
